<template>
  <div>
    <!-- Batch mode: a shelf QR was scanned, every article scan goes onto that shelf -->
    <div v-if="shelf" class="batch">
      <div class="batch-bar">
        <div class="batch-shelf">
          <span class="batch-label">Placing on</span>
          <span class="batch-name">{{ shelfName }}</span>
        </div>
        <b-button variant="success" size="lg" class="batch-done" @click="finishBatch">
          <b-icon-check2></b-icon-check2> DONE
        </b-button>
      </div>

      <b-form-input
        ref="kode"
        size="lg"
        v-model="code"
        v-on:keyup.enter="search"
        inputmode="none"
        autocomplete="off"
        class="batch-input"
        :placeholder="`Scan article → ${shelfName}`"></b-form-input>

      <div v-if="choices.length" class="batch-choices">
        <div class="batch-choices-title">Several products share EAN {{ choicesEan }} - pick one:</div>
        <b-button
          v-for="a in choices"
          :key="a.codename"
          variant="outline-primary"
          block
          @click="pick(a)">
          {{ a.name }} <small>({{ a.codename }})</small>
        </b-button>
      </div>

      <div class="batch-count">{{ placed.length }} article(s) · {{ totalScans }} piece(s) added</div>
      <div
        v-for="row in placed"
        :key="row.codename"
        :class="['batch-row', {
          'batch-ok': row.stock !== null, 'batch-moved': row.from, 'batch-error': row.failed,
        }]">
        <div class="batch-row-main">
          <div class="batch-row-name">{{ row.name }}</div>
          <div class="batch-row-meta">
            {{ row.codename }}<span v-if="row.stock !== null"> · stock {{ row.stock }}</span><span v-if="row.from" class="batch-row-moved"> · moved from {{ row.from }} (had {{ row.had }})</span>
          </div>
          <div v-if="row.failed" class="batch-row-failed">{{ row.failed }} NOT counted - scan again</div>
        </div>
        <span class="batch-row-times">+{{ row.times }}</span>
      </div>
    </div>

    <b-row v-else class="justify-content-center align-items-center manual-input">
      <b-col sm="12" md="6" class="scan-card">
        <div class="scan-card-icon"><b-icon-upc-scan></b-icon-upc-scan></div>
        <h3 class="scan-card-title">BD Stock</h3>
        <p class="scan-card-sub">Scan an article EAN to look it up, or a shelf QR to start placing articles</p>
        <b-form-input
        ref="kode"
        size="lg"
        v-model="code"
        v-on:keyup.enter="search"
        placeholder="Scan EAN code or shelf QR"></b-form-input>
        <br>
        <b-button
        @click="search"
        :disabled="code.length < 6"
        variant="primary"
        block size="lg">
          <b-icon-search class="mr-2"></b-icon-search>Manual search
        </b-button>
      </b-col>
  </b-row>
  </div>
</template>

<script>
import axios from 'axios';
import fixed from '@/fixed';
import { parseLocation } from '@/utils';

const sound = (file) => new Audio(file).play().catch(() => {});
const norm = (l) => String(l || '').trim().replace(/\s+/g, ' ').toUpperCase();

// Triple beep for "moved from another shelf", so it sounds different from ok/error.
// Generated with Web Audio, no sound file needed. Tune pitch/volume here if it's
// too quiet or harsh on the handhelds.
const WARN_HZ = 1300;
const WARN_VOLUME = 0.9;
let audioCtx;
const warnBeep = () => {
  try {
    audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    audioCtx.resume();
    [0, 0.2, 0.4].forEach((at) => {
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.type = 'square';
      osc.frequency.value = WARN_HZ;
      gain.gain.value = WARN_VOLUME;
      osc.connect(gain).connect(audioCtx.destination);
      const t = audioCtx.currentTime + at;
      osc.start(t);
      osc.stop(t + 0.13);
    });
  } catch (e) {
    // No Web Audio: the warning toast still shows.
  }
};

export default {
  data() {
    return {
      code: '',
      ticker: null,
      shelf: null,
      placed: [],
      choices: [],
      choicesEan: '',
      known: {}, // ean -> article, so repeat scans skip the lookup and the pick
    };
  },
  computed: {
    shelfName() {
      return this.shelfLocation.trim();
    },
    // Same "raf kolona red box" format Info.vue saves, so ProductsOnLocation finds it.
    shelfLocation() {
      const {
        rafa, kolona, red, box,
      } = this.shelf;
      return `${rafa} ${kolona} ${red} ${box}`;
    },
    totalScans() {
      return this.placed.reduce((sum, r) => sum + r.times, 0);
    },
  },
  mounted() {
    this.ticker = setInterval(() => {
      this.focusInput();
    }, 1000);
  },
  beforeDestroy() {
    if (this.ticker) clearInterval(this.ticker);
  },
  methods: {
    search() {
      const code = this.code.trim();
      this.code = '';

      // A shelf QR starts a batch, or switches the running one to a new shelf.
      const shelf = parseLocation(code);
      if (!shelf.error) {
        if (this.shelf) this.finishBatch();
        this.shelf = shelf;
        sound('/ok.mp3');
        return;
      }

      if (code.length < 6) return;
      if (!this.shelf) {
        this.$router.push({ name: 'quantity', params: { ean: code } });
        return;
      }
      this.placeEan(code);
    },
    async placeEan(ean) {
      if (this.known[ean]) {
        this.place(this.known[ean]);
        return;
      }
      const { shelf } = this;
      try {
        const { data: { data } } = await axios.get(`${fixed.API_URL}/admin/scan/${encodeURIComponent(ean)}`, { withCredentials: true });
        // A shelf QR / DONE landed while we were looking up: don't count this box on the wrong shelf.
        if (this.shelf !== shelf) {
          this.fail(`${ean} NOT counted - shelf changed, scan it again`);
          return;
        }
        if (!data || !data.length) {
          this.fail(`No article with EAN ${ean}`);
          return;
        }
        if (data.length > 1) {
          this.choices = data;
          this.choicesEan = ean;
          sound('/error.mp3');
          return;
        }
        [this.known[ean]] = data;
        await this.place(this.known[ean]);
      } catch (e) {
        this.fail(`Lookup failed for ${ean}`);
      }
    },
    pick(article) {
      this.known[this.choicesEan] = article;
      this.choices = [];
      this.place(article);
    },
    // Every scan is one piece put on the shelf: +1 stock and location set to this shelf.
    async place(article) {
      let row = this.placed.find((r) => r.codename === article.codename);
      if (!row) {
        // localLocation is never cleared when stock hits 0, so only a shelf that
        // still has pieces counts as a move. localCount is omitted from JSON when 0.
        const had = article.localCount || 0;
        const from = had > 0 ? norm(article.localLocation) : '';
        row = {
          codename: article.codename,
          name: article.name,
          times: 0,
          failed: 0,
          stock: null,
          had,
          from: from && from !== norm(this.shelfLocation) ? from : '',
        };
      }
      this.placed = [row, ...this.placed.filter((r) => r !== row)];

      // Read before the await: DONE may close the batch while the save is in flight.
      const location = this.shelfLocation;
      try {
        const { data } = await axios.put(`${fixed.API_URL}/admin/stock-in`, { sku: article.codename, location }, { withCredentials: true });
        row.times += 1;
        row.stock = data.localCount;
        // A successful rescan makes up for an earlier failed one.
        if (row.failed) row.failed -= 1;
        // First piece of an article that was on another shelf: make the move heard.
        if (row.from && row.times === 1) {
          warnBeep();
          this.$toast.warning(`${row.codename} moved from ${row.from} (had ${row.had}) to ${location.trim()}`);
        } else {
          sound('/ok.mp3');
        }
      } catch (e) {
        row.failed += 1;
        this.fail(`${article.codename} NOT counted - scan again`);
      }
    },
    fail(msg) {
      this.$toast.error(msg);
      sound('/error.mp3');
    },
    finishBatch() {
      const failed = this.placed.reduce((sum, r) => sum + r.failed, 0);
      if (failed) this.$toast.error(`${failed} piece(s) NOT counted on ${this.shelfName}`);
      this.$toast.success(`${this.totalScans} piece(s) added to ${this.shelfName}`);
      this.shelf = null;
      this.placed = [];
      this.choices = [];
      this.known = {};
    },
    focusInput() {
      if (this.$refs.kode) this.$refs.kode.focus();
    },
  },
};
</script>
<style lang="css">
.manual-input {
  height: 93vh;
  padding: 20px;
}

.batch {
  max-width: 720px;
  margin: 0 auto;
  padding: 0.5rem 0 2rem;
}

/* Shelf + DONE stay in view while the scanned list grows */
.batch-bar {
  position: sticky;
  /* Published by Navbar.vue; 0 when the navbar isn't rendered. */
  top: var(--navbar-height, 0px);
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background-color: var(--surface);
  border: 2px solid var(--success);
  border-radius: var(--radius);
  box-shadow: var(--shadow-sm);
  padding: 0.75rem;
}

.batch-shelf {
  flex: 1;
  min-width: 0;
}

.batch-label {
  display: block;
  font-size: 0.75rem;
  text-transform: uppercase;
  color: var(--text-muted);
}

.batch-name {
  display: block;
  font-size: 1.6rem;
  font-weight: bold;
  line-height: 1.1;
}

.batch-done {
  min-height: 56px;
  min-width: 110px;
  font-weight: bold;
}

.batch-input {
  margin-top: 0.75rem;
  border: 2px solid var(--border-strong);
}

.batch-choices {
  margin-top: 0.75rem;
  padding: 0.75rem;
  border: 2px solid var(--warning);
  border-radius: var(--radius-sm);
}

.batch-choices-title {
  font-weight: bold;
  margin-bottom: 0.5rem;
}

.batch-count {
  margin: 0.75rem 0 0.5rem;
  font-size: 0.85rem;
  color: var(--text-muted);
}

.batch-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: var(--surface);
  border: 1px solid var(--border);
  border-left: 5px solid var(--info);
  border-radius: var(--radius-sm);
  padding: 0.6rem;
  margin-bottom: 0.5rem;
}

.batch-row.batch-ok {
  border-left-color: var(--success);
}

.batch-row.batch-moved {
  border-left-color: var(--warning);
}

.batch-row-moved {
  color: var(--warning);
  font-weight: bold;
}

.batch-row.batch-error {
  border-left-color: var(--danger);
}

.batch-row-failed {
  color: var(--danger);
  font-weight: bold;
}

.batch-row-main {
  flex: 1;
  min-width: 0;
}

.batch-row-name {
  font-weight: 600;
  overflow-wrap: anywhere;
}

.batch-row-meta {
  font-size: 0.8rem;
  color: var(--text-muted);
}

.batch-row-times {
  font-size: 1.25rem;
  font-weight: bold;
}
</style>
